const { initHistory, updateScores } = require('./RaceTracker');
const { getRule } = require('./RuleLogic');

function normalizeDraftPlayer(player) {
  const playerId = String(player.playerId || '');
  const characterId = String(player.characterId || '0');
  return {
    playerId,
    characterId,
    fullName: player.fullName ||
      `${player.firstName || ''} ${player.lastName || ''}`.trim() || `Player ${playerId}`,
    ...(player.elo === undefined ? {} : { elo: Number(player.elo) }),
  };
}

function createGameplayDraft({
  title,
  ruleInfo,
  hardcore,
  engineClassId,
  roster,
  savedRoster = roster,
}) {
  const normalizedRoster = roster.map(normalizeDraftPlayer);
  const normalizedSavedRoster = savedRoster.map(normalizeDraftPlayer);
  return {
    schemaVersion: 1,
    setup: {
      title,
      ruleId: String(ruleInfo.ruleId),
      hardcore,
      engineClassId: String(engineClassId),
      raceCount: Number(ruleInfo.raceCount),
      groupSize: Number(ruleInfo.groupSize),
      roster: normalizedRoster,
      savedRoster: normalizedSavedRoster,
    },
    races: [],
    courseIndex: 0,
    currentPlayers: normalizedRoster.slice(0, Number(ruleInfo.groupSize)),
    tieBreak: { active: false, playerIds: [] },
  };
}

function isGameplayDraftV1(value) {
  const isRecord = (entry) => entry !== null && typeof entry === 'object' && !Array.isArray(entry);
  const isString = (entry) => typeof entry === 'string';
  const isArrayOf = (entry, predicate) => Array.isArray(entry) && Array.from(entry).every(predicate);
  const isDraftPlayer = (entry) => isRecord(entry) && isString(entry.playerId) &&
    isString(entry.characterId) && isString(entry.fullName) &&
    (entry.elo === undefined || (typeof entry.elo === 'number' && Number.isFinite(entry.elo)));
  const isRacePlayer = (entry) => isRecord(entry) && isString(entry.playerId) &&
    isString(entry.characterId) && Number.isInteger(entry.place) && entry.place > 0;
  const isRace = (entry) => isRecord(entry) && isString(entry.courseId) &&
    isString(entry.engineClassId) && isArrayOf(entry.racePlayers, isRacePlayer) &&
    (entry.racePhase === undefined || ['race', 'qualifier', 'final'].includes(entry.racePhase));

  if (!isRecord(value) || value.schemaVersion !== 1 || !isRecord(value.setup) ||
      !isRecord(value.tieBreak)) return false;
  const setup = value.setup;
  const valid = isString(setup.title) && isString(setup.ruleId) &&
    typeof setup.hardcore === 'boolean' && isString(setup.engineClassId) &&
    Number.isInteger(setup.raceCount) && setup.raceCount >= 0 &&
    Number.isInteger(setup.groupSize) && setup.groupSize > 0 &&
    isArrayOf(setup.roster, isDraftPlayer) &&
    (setup.savedRoster === undefined || isArrayOf(setup.savedRoster, isDraftPlayer)) &&
    isArrayOf(value.races, isRace) && Number.isInteger(value.courseIndex) && value.courseIndex >= 0 &&
    isArrayOf(value.currentPlayers, isDraftPlayer) && typeof value.tieBreak.active === 'boolean' &&
    isArrayOf(value.tieBreak.playerIds, isString);
  if (!valid) return false;
  const bracket = value.tieBreak.bracket;
  if (bracket === undefined) return true;
  if (!isRecord(bracket) || !['qualifying', 'final'].includes(bracket.stage) ||
      !isArrayOf(bracket.heats, heat => isArrayOf(heat, id => isString(id) && id.length > 0) && heat.length >= 2 && heat.length <= 4) ||
      bracket.heats.length !== 2 || !Number.isInteger(bracket.heatIndex) ||
      !isArrayOf(bracket.winners, isString) || !isArrayOf(bracket.qualifyingRaces, isRace) ||
      bracket.winners.length !== bracket.heatIndex || bracket.qualifyingRaces.length !== bracket.heatIndex ||
      (bracket.stage === 'qualifying' && (bracket.heatIndex < 0 || bracket.heatIndex > 1)) ||
      (bracket.stage === 'final' && bracket.heatIndex !== 2)) return false;
  const ids = bracket.heats.flat();
  const knownIds = new Set([...setup.roster, ...(setup.savedRoster || [])].map(player => player.playerId));
  const tiedIds = value.tieBreak.playerIds;
  const currentIds = value.currentPlayers.map(player => player.playerId);
  if (ids.length < 5 || ids.length > 8 || new Set(ids).size !== ids.length ||
      ids.some(id => !knownIds.has(id)) || new Set(tiedIds).size !== tiedIds.length ||
      new Set(bracket.winners).size !== bracket.winners.length ||
      Math.abs(bracket.heats[0].length - bracket.heats[1].length) > 1 ||
      value.courseIndex < setup.raceCount ||
      (tiedIds.length !== 0 && (tiedIds.length !== ids.length || tiedIds.some(id => !ids.includes(id))))) return false;
  for (const [index, race] of bracket.qualifyingRaces.entries()) {
    const heat = bracket.heats[index];
    // Legacy drafts omit phases; hydration tags these before strict IPC writes.
    if ((race.racePhase !== undefined && race.racePhase !== 'qualifier') ||
        race.racePlayers.length !== heat.length ||
        new Set(race.racePlayers.map(player => player.playerId)).size !== heat.length ||
        race.racePlayers.some(player => !heat.includes(player.playerId)) ||
        new Set(race.racePlayers.map(player => player.place)).size !== heat.length ||
        race.racePlayers.some(player => player.place > heat.length) ||
        race.racePlayers.find(player => player.place === 1)?.playerId !== bracket.winners[index]) return false;
  }
  if (value.tieBreak.active) {
    const expected = bracket.stage === 'qualifying' ? bracket.heats[bracket.heatIndex] : bracket.winners;
    return tiedIds.length === ids.length && currentIds.length === expected.length &&
      new Set(currentIds).size === expected.length && currentIds.every(id => expected.includes(id));
  }
  return bracket.stage === 'final' && currentIds.length === setup.groupSize &&
    new Set(currentIds).size === currentIds.length && currentIds.every(id => knownIds.has(id));
}

function getAuthoritativeRaceCount(draft) {
  const rosterSize = draft?.setup?.roster?.length;
  const groupSize = Number(draft?.setup?.groupSize);
  const rule = getRule(rosterSize, groupSize);

  if (!rule || String(rule.ruleId) !== String(draft?.setup?.ruleId)) {
    throw new Error('The Grand Prix rule does not match its roster and group size.');
  }
  return Number(rule.raceCount);
}

function shouldEvaluateTieBreak(draft) {
  return Boolean(draft?.tieBreak?.active) ||
    Number(draft?.courseIndex) >= getAuthoritativeRaceCount(draft);
}

function deriveScoreHistory(draft) {
  let history = initHistory(draft.setup.roster);
  draft.races.filter((race) => race.racePhase !== 'qualifier').forEach((race) => {
    history = updateScores(
      history,
      race,
      draft.setup.ruleId,
      draft.setup.savedRoster || draft.setup.roster,
    );
  });
  return history;
}

function applyCharactersToRoster(roster, racePlayers) {
  const characters = new Map(racePlayers.map((player) => [
    String(player.playerId),
    String(player.characterId),
  ]));
  return roster.map((player) => characters.has(String(player.playerId)) ? {
    ...player,
    characterId: characters.get(String(player.playerId)),
  } : player);
}

function isCompleteHeat(currentPlayers, racePlayers) {
  if (currentPlayers.length !== racePlayers.length) return false;
  const expected = new Set(currentPlayers.map((player) => String(player.playerId)));
  return racePlayers.every((player) => expected.has(String(player.playerId))) &&
    new Set(racePlayers.map((player) => String(player.playerId))).size === expected.size;
}

function resolveLineupPlayer(roster, playerId) {
  const selected = roster.find(
    (player) => String(player.playerId) === String(playerId),
  );
  return selected ? normalizeDraftPlayer(selected) : null;
}

function resolveLineupPlayers(roster, playerIds) {
  return playerIds
    .map((playerId) => resolveLineupPlayer(roster, playerId))
    .filter(Boolean);
}

module.exports = { normalizeDraftPlayer, createGameplayDraft, isGameplayDraftV1, getAuthoritativeRaceCount, shouldEvaluateTieBreak, deriveScoreHistory, applyCharactersToRoster, isCompleteHeat, resolveLineupPlayer, resolveLineupPlayers };
